import styled from '@emotion/styled/macro';
import { BaseButton, Variables } from '@enter/core';
import Icon from '@mdi/react';
import { Link } from 'react-router-dom';

export const Body = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    flex: 1 1 auto;
    align-self: stretch;
    overflow: hidden;
    box-sizing: border-box;
`;

export const Scroll = styled.div`
    flex: 1 1 0;
    min-height: 0;
    overflow-x: auto;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 18px 22px;

    /* Flex column + overflow:auto otherwise shrinks header sections (Issues) to 0 */
    & > * {
        flex-shrink: 0;
    }
`;

export const PatientRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

export const Avatar = styled.div`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: ${Variables.ThemeLight.Background1};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Variables.ThemeLight.TextMedium};
    flex: none;
`;

export const PatientName = styled.div`
    font-size: 17px;
    font-weight: 600;
    line-height: 1.2;
    color: ${Variables.ThemeLight.TextStrong};
`;

/** Patient name as a link to the statement page — same typography, underline on hover. */
export const PatientNameLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.2;
    color: ${Variables.ThemeLight.TextStrong};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

/** Open-in-new affordance, sized as the claim flow's BreadcrumbOpenIcon. */
export const PatientNameOpenIcon = styled(Icon)`
    color: ${Variables.ThemeLight.TextXMild};
    width: 13px;
    height: 13px;
    flex-shrink: 0;
`;

export const PatientMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
    margin-top: 2px;
    font-variant-numeric: tabular-nums;
`;

export const MetaDot = styled.span`
    color: ${Variables.ThemeLight.TextXMild};
    padding: 0 2px;
`;

export const ClaimLink = styled(Link)`
    color: ${Variables.ThemeLight.PrimaryConstant6};
    font-variant-numeric: tabular-nums;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

export const MetaRight = styled.div`
    margin-left: auto;
    text-align: right;
    font-size: 12px;
    line-height: 1.5;
    color: ${Variables.ThemeLight.TextMild};
    flex: none;
`;

export const Strong = styled.span`
    color: ${Variables.ThemeLight.TextStrong};
    font-weight: 500;
    font-variant-numeric: tabular-nums;
`;

export const AmountStrip = styled.div`
    display: flex;
    flex-shrink: 0;
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    overflow: hidden;
    min-height: 52px;
`;

export const AmountStripCell = styled.div`
    flex: 1;
    padding: 9px 13px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    border-left: 1px solid ${Variables.ThemeLight.Border1};

    &:first-of-type {
        border-left: none;
    }
`;

export const AmountStripLabel = styled.span`
    font-size: 10px;
    font-weight: 500;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMild};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

export const AmountStripValue = styled.span<{ $muted?: boolean }>`
    font-size: 14px;
    font-weight: 600;
    color: ${p => (p.$muted ? Variables.ThemeLight.TextMedium : Variables.ThemeLight.TextStrong)};
    font-variant-numeric: tabular-nums;
`;

export const Issues = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

export const IssuesHeader = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-right: 2px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMild};
    text-transform: uppercase;
    letter-spacing: 0.05em;

    svg {
        fill: ${Variables.ThemeLight.TextMild};
    }
`;

export const IssueChip = styled.span<{ $tone?: 'warn' | 'bad' | 'info' }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 100%;
    border: 1px solid
        ${p => {
            if (p.$tone === 'bad') return Variables.ThemeLight.WarningMild1;
            if (p.$tone === 'info') return Variables.ThemeLight.PrimaryMild2;
            return Variables.ThemeLight.AttentionMild1;
        }};
    background: ${p => {
        if (p.$tone === 'bad') return Variables.ThemeLight.WarningMild3;
        if (p.$tone === 'info') return Variables.ThemeLight.PrimaryMild2;
        return Variables.ThemeLight.AttentionMild3;
    }};
    border-radius: 5px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
    color: ${p => {
        if (p.$tone === 'bad') return Variables.ThemeLight.WarningInverted3;
        if (p.$tone === 'info') return Variables.ThemeLight.PrimaryConstant6;
        return Variables.ThemeLight.AttentionInverted3;
    }};

    svg {
        flex: none;
        fill: currentColor;
    }
`;

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 9px;
    flex-wrap: wrap;
`;

export const SectionLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const TableWrap = styled.div`
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: hidden;
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 920px;
`;

export const Th = styled.th`
    text-align: left;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMedium};
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: ${Variables.ThemeLight.Background1};
    white-space: nowrap;

    &[data-right='true'] {
        text-align: right;
    }
`;

export const Td = styled.td`
    padding: 7px 12px;
    border-top: 1px solid ${Variables.ThemeLight.Border1};
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMedium};
    vertical-align: middle;

    &[data-right='true'] {
        text-align: right;
        padding-left: 8px;
        padding-right: 8px;
    }
`;

export const CptCode = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const CptMeta = styled.div`
    font-size: 11px;
    font-weight: 400;
    color: ${Variables.ThemeLight.TextMild};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

/** Compact amount field matching Chris mockup — not BaseInput (form field is too tall). */
export const AmountInput = styled.input`
    width: 74px;
    text-align: right;
    border: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background2};
    border-radius: 6px;
    height: 32px;
    padding: 0 9px;
    font-size: 13px;
    font-weight: 400;
    color: ${Variables.ThemeLight.TextStrong};
    outline: none;
    font-variant-numeric: tabular-nums;
    box-sizing: border-box;

    &:focus {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 3px ${Variables.ThemeLight.PrimaryMild2};
    }
`;

export const ExpandBtn = styled(BaseButton)`
    && {
        min-width: 26px;
        width: 26px;
        height: 26px;
        padding: 0;
        border: none;
        border-radius: 6px;
        color: ${Variables.ThemeLight.TextMedium};
        background: transparent;
    }

    &&:hover:not(:disabled) {
        background: ${Variables.ThemeLight.Background1};
    }
`;

export const Postings = styled.div`
    padding: 11px 14px 13px 42px;
    background: ${Variables.ThemeLight.Background1};
    border-top: 1px solid ${Variables.ThemeLight.Border1};
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const PostingsHeader = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 8px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMild};
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

export const PostingsHeaderMeta = styled.span`
    margin-left: auto;
    font-size: 11px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: normal;
    color: ${Variables.ThemeLight.TextMild};
    font-variant-numeric: tabular-nums;
`;

export const PostingCard = styled.div`
    background: ${Variables.ThemeLight.Background3};
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 7px;
    padding: 8px 11px;
`;

export const PostingTop = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;
`;

export const PostingMeta = styled.div`
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMild};
    font-variant-numeric: tabular-nums;
`;

export const PostingAmounts = styled.div`
    margin-left: auto;
    display: flex;
    gap: 18px;
`;

export const PostingAmt = styled.div`
    text-align: right;
    min-width: 64px;
`;

export const PostingAmtLabel = styled.div`
    font-size: 10px;
    font-weight: 500;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMild};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

export const PostingAmtValue = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
    margin-top: 3px;
`;

export const Remark = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 4px;
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const RemarkCode = styled.span`
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
    width: 56px;
    flex: none;
`;

export const Orphans = styled.div`
    margin-top: 12px;
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    overflow: hidden;
`;

export const OrphansHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 13px;
    background: #fbefd9;
    border-bottom: 1px solid #f0d79b;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: #8a5e1c;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

export const OrphanRow = styled.div`
    padding: 10px 13px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};

    &:last-of-type {
        border-bottom: none;
    }
`;

export const OrphansHint = styled.div`
    padding: 9px 13px;
    font-size: 12px;
    line-height: 1.45;
    color: ${Variables.ThemeLight.TextMild};
    border-top: 1px solid ${Variables.ThemeLight.Border1};
`;

export const Discounts = styled.div`
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    overflow: hidden;
`;

export const DiscountsHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 13px;
    background: ${Variables.ThemeLight.Background1};
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMedium};
    text-transform: uppercase;
    letter-spacing: 0.05em;

    svg {
        fill: ${Variables.ThemeLight.TextMedium};
    }
`;

export const DiscountRow = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 9px 13px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
`;

export const DiscountCodeInput = styled.input`
    width: 78px;
    flex: none;
    border: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background2};
    border-radius: 6px;
    height: 32px;
    padding: 0 9px;
    font-size: 13px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextStrong};
    outline: none;
    font-variant-numeric: tabular-nums;
    box-sizing: border-box;

    &:focus {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 3px ${Variables.ThemeLight.PrimaryMild2};
    }
`;

export const DiscountDescInput = styled.input`
    flex: 1;
    min-width: 0;
    border: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background2};
    border-radius: 6px;
    height: 32px;
    padding: 0 10px;
    font-size: 13px;
    color: ${Variables.ThemeLight.TextStrong};
    outline: none;
    box-sizing: border-box;

    &:focus {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 3px ${Variables.ThemeLight.PrimaryMild2};
    }
`;

export const DiscountAmountInput = styled(AmountInput)`
    width: 88px;
    font-weight: 500;
`;

export const DiscountsHint = styled.div`
    padding: 8px 13px;
    font-size: 12px;
    line-height: 1.45;
    color: ${Variables.ThemeLight.TextMild};
`;

export const DiscountsEmpty = styled.div`
    padding: 11px 13px;
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const NoteArea = styled.textarea`
    width: 100%;
    border: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background2};
    border-radius: 8px;
    padding: 10px 12px;
    /* At least 2 text rows (padding + 2 × line-height) */
    min-height: calc(20px + 1.4em * 2);
    height: calc(20px + 1.4em * 2);
    resize: vertical;
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextStrong};
    outline: none;
    font-family: inherit;
    box-sizing: border-box;

    &:focus {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 3px ${Variables.ThemeLight.PrimaryMild2};
    }
`;

export const PaymentsBlock = styled.div`
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    overflow: hidden;
`;

export const PaymentsHeader = DiscountsHeader;

export const PaymentsHeaderMeta = styled.span`
    margin-left: auto;
    font-size: 12px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: normal;
    color: ${Variables.ThemeLight.TextMild};
    font-variant-numeric: tabular-nums;
`;

export const PaymentsHeaderAmt = styled.span`
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const ManageAllocButton = styled(BaseButton)`
    && {
        height: 28px;
        padding: 0 10px;
        gap: 5px;
        min-width: 0;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        flex: none;
    }

    && svg {
        width: 15px;
        height: 15px;
    }
`;

export const PaymentsEmpty = styled.div`
    padding: 11px 13px;
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const PaymentRow = styled.div`
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 9px 13px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};

    &:last-of-type {
        border-bottom: none;
    }
`;

export const PaymentMethod = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const PaymentMeta = styled.div`
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMild};
    font-variant-numeric: tabular-nums;
`;

export const PaymentAmount = styled.div`
    margin-left: auto;
    text-align: right;
    flex: none;
`;

export const PaymentAllocated = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const PaymentBreakdown = styled.div`
    font-size: 11px;
    color: ${Variables.ThemeLight.TextMild};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const PaymentsAvailable = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 9px 13px;
    background: ${Variables.ThemeLight.AttentionMild3};
    border-top: 1px solid ${Variables.ThemeLight.AttentionMild1};
    font-size: 12px;
    line-height: 1.45;
    color: ${Variables.ThemeLight.AttentionInverted3};

    svg {
        flex: none;
        fill: ${Variables.ThemeLight.AttentionInverted3};
    }
`;

export const AllocScrim = styled.div`
    position: fixed;
    inset: 0;
    z-index: 120;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: ${Variables.ThemeLight.OverlayDark4};
`;

export const AllocCard = styled.div`
    width: 920px;
    max-width: 100%;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: ${Variables.ThemeLight.Background3};
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 14px;
    outline: none;
`;

export const AllocHead = styled.div`
    flex: none;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 18px 22px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
`;

export const AllocTitle = styled.div`
    font-size: 17px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const AllocSub = styled.div`
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMild};
    margin-top: 3px;
`;

export const AllocBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 22px;
`;

export const AllocTableWrap = styled.div`
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 8px;
    overflow-x: auto;
`;

export const AllocTable = styled.table`
    width: 100%;
    min-width: 860px;
    border-collapse: collapse;
`;

export const AllocTh = styled.th`
    text-align: left;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMedium};
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background1};

    &[data-right='true'] {
        text-align: right;
    }
`;

export const AllocTd = styled.td`
    padding: 9px 12px;
    font-size: 13px;
    color: ${Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};

    &[data-right='true'] {
        text-align: right;
    }
`;

export const AllocUseCell = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    justify-content: flex-end;
`;

export const AllocMaxButton = styled(BaseButton)`
    && {
        height: 30px;
        padding: 0 8px;
        min-width: 0;
        font-size: 12px;
        font-weight: 500;
        flex: none;
    }
`;

export const AllocUseInput = styled.input<{ $over?: boolean }>`
    width: 96px;
    flex: none;
    text-align: right;
    border: 1px solid
        ${p => (p.$over ? Variables.ThemeLight.WarningMild1 : Variables.ThemeLight.Border1)};
    background: ${Variables.ThemeLight.Background3};
    border-radius: 6px;
    height: 32px;
    padding: 0 9px;
    font-size: 13px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextStrong};
    outline: none;
    font-variant-numeric: tabular-nums;
    box-sizing: border-box;

    &:focus {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 3px ${Variables.ThemeLight.PrimaryMild2};
    }
`;

export const AllocWarn = styled.div`
    margin-top: 4px;
    font-size: 11px;
    font-weight: 500;
    color: ${Variables.ThemeLight.WarningInverted3};
    white-space: nowrap;
`;

export const AllocRegenNote = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-top: 12px;
    border: 1px solid ${Variables.ThemeLight.AttentionMild1};
    background: ${Variables.ThemeLight.AttentionMild3};
    border-radius: 8px;
    padding: 11px 13px;
    font-size: 13px;
    line-height: 1.45;
    color: ${Variables.ThemeLight.AttentionInverted3};

    svg {
        flex: none;
        fill: ${Variables.ThemeLight.AttentionInverted3};
    }
`;

export const AllocFooter = styled.div`
    flex: none;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 14px 22px;
    border-top: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background1};
`;

export const AllocImpact = styled.div`
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const AllocFooterRight = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const AllocCloseButton = styled(BaseButton)`
    && {
        margin-left: auto;
        min-width: 0;
        padding: 2px;
        color: ${Variables.ThemeLight.TextMild};
    }
`;

export const PaymentsApplied = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 13px;
    background: ${Variables.ThemeLight.Background1};
    border-top: 1px solid ${Variables.ThemeLight.Border1};
    font-size: 13px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const PaymentsAppliedAmt = styled.span`
    margin-left: auto;
    font-size: 14px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
`;

export const PaymentsOverage = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 9px 13px;
    border-top: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.AttentionMild3};
    color: ${Variables.ThemeLight.AttentionInverted3};
    font-size: 12px;
    font-weight: 500;
    line-height: 1.45;

    & > svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

export const BalanceDueBanner = styled.div`
    display: flex;
    align-items: center;
    margin-top: 10px;
    padding: 11px 13px;
    border: 1px solid ${Variables.ThemeLight.PrimaryInverted7};
    background: ${Variables.ThemeLight.PrimaryOpacity3};
    border-radius: 8px;
`;

export const BalanceDueLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const BalanceDueValue = styled.span`
    margin-left: auto;
    font-size: 18px;
    font-weight: 600;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    font-variant-numeric: tabular-nums;
`;

export const Footer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
    padding: 13px 22px;
    border-top: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background1};
    position: sticky;
    bottom: 0;
    z-index: 1;
`;

export const FooterRight = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const footerButton = `
    && {
        height: 40px;
        min-width: 0;
        flex: none;
        white-space: nowrap;
        border-radius: 7px;
        padding: 0 14px;
        font-size: 14px;
        font-weight: 500;
        gap: 6px;
    }

    && svg {
        width: 18px;
        height: 18px;
        margin: 0;
        flex: none;
    }

    && svg ~ span,
    && span + svg {
        margin-left: 0;
    }
`;

/** Chris footer Cancel: outlined, muted text. */
export const FooterCancelButton = styled(BaseButton)`
    ${footerButton}

    && {
        color: ${Variables.ThemeLight.TextMedium};
        background: ${Variables.ThemeLight.Background3};
        border: 1px solid ${Variables.ThemeLight.Border1};
    }

    && svg {
        fill: ${Variables.ThemeLight.TextMedium};
    }

    &&:hover:not(:disabled) {
        background: ${Variables.ThemeLight.NeutralMild3};
    }
`;

/** Chris footer Reject: outlined warning, not filled. */
export const FooterRejectButton = styled(BaseButton)`
    ${footerButton}

    && {
        color: ${Variables.ThemeLight.WarningInverted3};
        background: ${Variables.ThemeLight.Background3};
        border: 1px solid ${Variables.ThemeLight.WarningMild1};
    }

    && svg {
        fill: ${Variables.ThemeLight.WarningInverted3};
    }

    &&:hover:not(:disabled) {
        background: ${Variables.ThemeLight.WarningMild3};
    }
`;

/** Chris footer Clear allocation. */
export const FooterSecondaryButton = styled(BaseButton)`
    ${footerButton}

    && {
        color: ${Variables.ThemeLight.TextStrong};
        background: ${Variables.ThemeLight.Background3};
        border: 1px solid ${Variables.ThemeLight.Border1};
    }

    && svg {
        fill: ${Variables.ThemeLight.TextStrong};
    }

    &&:hover:not(:disabled) {
        background: ${Variables.ThemeLight.NeutralMild3};
    }
`;

/** Chris: Confirm & next — primary-tint outlined, not the solid Confirm balance. */
export const ConfirmNextButton = styled(BaseButton)`
    ${footerButton}

    && {
        padding: 0 13px;
        font-weight: 600;
        gap: 7px;
        background: ${Variables.ThemeLight.PrimaryMild1};
        color: ${Variables.ThemeLight.PrimaryConstant6};
        border: 1px solid ${Variables.ThemeLight.PrimaryMild2};
    }

    && svg {
        fill: ${Variables.ThemeLight.PrimaryConstant6};
    }

    &&:hover:not(:disabled) {
        background: ${Variables.ThemeLight.PrimaryMild2};
    }
`;

/** Chris: Confirm balance — solid primary + arrow. */
export const FooterConfirmButton = styled(BaseButton)`
    ${footerButton}

    && {
        font-weight: 600;
        gap: 7px;
        background: ${Variables.ThemeLight.PrimaryConstant6};
        color: ${Variables.ThemeLight.Background3};
        border: 1px solid transparent;
    }

    && svg {
        fill: ${Variables.ThemeLight.Background3};
    }

    &&:hover:not(:disabled) {
        background: ${Variables.ThemeLight.PrimaryInverted5};
    }
`;

export const EmptyHint = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-start;
    border: 1px dashed ${Variables.ThemeLight.Border1};
    border-radius: 7px;
    padding: 10px 11px;
    font-size: 12px;
    line-height: 1.45;
    color: ${Variables.ThemeLight.TextMedium};
    background: ${Variables.ThemeLight.Background3};
`;

export const Loading = styled.div`
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    text-align: center;
    color: ${Variables.ThemeLight.TextMild};
`;

export const TotalRowLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const TotalMuted = styled.span`
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
    font-variant-numeric: tabular-nums;
`;

export const TotalValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextMedium};
    font-variant-numeric: tabular-nums;
`;

export const TotalBalance = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    font-variant-numeric: tabular-nums;
`;
